import React, { useEffect, useState } from 'react';
import { apiFetch } from './utils/api';

const DOC_TYPE_LABELS = {
  invoice: 'Invoice',
  job_sheet: 'Job Sheet',
  purchase_order: 'Purchase Order',
  quote: 'Quote',
  prospect_quote: 'Prospect Quote',
  ppm_quote: 'PPM Quote',
  risk_assessment: 'Risk Assessment',
  service_report: 'Service Report',
  completion_certificate: 'Completion Certificate',
  delivery_note: 'Delivery Note',
  sales_order: 'Sales Order',
  ppm_contract: 'PPM Contract',
  other: 'Other',
};

function isSampleTemplate(template) {
  return String(template.original_filename || '').toLowerCase().startsWith('sample_')
    && String(template.description || '').toLowerCase().startsWith('sample ');
}

function pickInitialTemplate(templates) {
  const customTemplates = templates
    .filter((template) => !isSampleTemplate(template))
    .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));

  return customTemplates.find((template) => template.is_default)
    || customTemplates[0]
    || templates.find((template) => template.is_default)
    || templates[0]
    || null;
}

function DocumentTemplateSelector({
  isOpen,
  onClose,
  documentType,
  placementKey,
  data,
  onGenerate,
  showEmailAction = false,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [resolvedType, setResolvedType] = useState(documentType);
  const [resolvedLabel, setResolvedLabel] = useState('');

  useEffect(() => {
    if (isOpen && (documentType || placementKey)) {
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, documentType, placementKey]);

  async function fetchTemplates() {
    setLoading(true);
    setError(null);
    try {
      let typeToFetch = documentType;
      let labelToShow = '';

      if (placementKey) {
        const placementResponse = await apiFetch(`/api/documents/placements/${encodeURIComponent(placementKey)}/resolve`);
        const placement = await placementResponse.json();
        if (!placementResponse.ok) {
          throw new Error(placement.error || 'Document placement is not available.');
        }
        typeToFetch = placement.library_key || documentType;
        labelToShow = placement.library_label || placement.label || '';
      }

      setResolvedType(typeToFetch);
      setResolvedLabel(labelToShow);

      const response = await apiFetch(`/api/documents/templates?type=${encodeURIComponent(typeToFetch)}`);
      const result = await response.json();
      if (!Array.isArray(result)) {
        throw new Error('Template library returned an unexpected response.');
      }

      setTemplates(result);
      setSelectedTemplate(pickInitialTemplate(result));
    } catch (err) {
      setTemplates([]);
      setSelectedTemplate(null);
      setError(err.message || 'Failed to load templates.');
    } finally {
      setLoading(false);
    }
  }

  function handleGenerate(action) {
    if (!selectedTemplate) {
      setError('Please select a template.');
      return;
    }
    onGenerate(selectedTemplate, action, data);
  }

  if (!isOpen) return null;

  const typeLabel = resolvedLabel || DOC_TYPE_LABELS[resolvedType] || DOC_TYPE_LABELS[documentType] || resolvedType || documentType || 'Document';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-[#082547] text-white">
          <h3 className="font-semibold text-lg">Select {typeLabel} Template</h3>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl leading-none">
            x
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#082547]"></div>
              <span className="ml-3 text-gray-600">Loading templates...</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">No {typeLabel} templates available</p>
              <p className="text-sm text-gray-400 mb-4">
                Upload a template in Design Hub &gt; Document Templates.
              </p>
              <button onClick={onClose} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300">
                Close
              </button>
            </div>
          ) : (
            <div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose Template
                </label>
                <div className="space-y-2">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => setSelectedTemplate(template)}
                      className={`w-full text-left border-2 rounded-lg p-4 cursor-pointer transition ${
                        selectedTemplate?.id === template.id
                          ? 'border-[#082547] bg-[#082547]/5'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                            <span className="truncate">{template.name}</span>
                            {template.is_default && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                Default
                              </span>
                            )}
                            {isSampleTemplate(template) && (
                              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                                Sample
                              </span>
                            )}
                          </h4>
                          {template.description && (
                            <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                          )}
                          {Array.isArray(template.merge_fields) && template.merge_fields.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {template.merge_fields.slice(0, 5).map((field, index) => (
                                <code key={index} className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                                  {typeof field === 'string' ? field : field.name || field.field || JSON.stringify(field)}
                                </code>
                              ))}
                              {template.merge_fields.length > 5 && (
                                <span className="text-xs text-gray-400">+{template.merge_fields.length - 5}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                          selectedTemplate?.id === template.id ? 'border-[#082547] bg-[#082547]' : 'border-gray-300'
                        }`}>
                          {selectedTemplate?.id === template.id && (
                            <span className="w-2 h-2 rounded-full bg-white"></span>
                          )}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-200">
                <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition">
                  Cancel
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleGenerate('preview')}
                    disabled={!selectedTemplate}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 text-sm font-medium"
                  >
                    Preview
                  </button>
                  <button
                    onClick={() => handleGenerate('docx')}
                    disabled={!selectedTemplate}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition disabled:opacity-50 text-sm font-medium"
                  >
                    Word (.docx)
                  </button>
                  <button
                    onClick={() => handleGenerate('pdf')}
                    disabled={!selectedTemplate}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50 text-sm font-medium"
                  >
                    Download PDF
                  </button>
                  <button
                    onClick={() => handleGenerate('print')}
                    disabled={!selectedTemplate}
                    className="bg-[#082547] text-white px-4 py-2 rounded-lg hover:bg-[#0a2d5c] transition disabled:opacity-50 text-sm font-medium"
                  >
                    Print
                  </button>
                  {showEmailAction && (
                    <button
                      onClick={() => handleGenerate('email')}
                      disabled={!selectedTemplate}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm font-medium"
                    >
                      Email
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentTemplateSelector;
